<script lang="ts" setup>
import { ref } from 'vue'
import './_index.scss'

import RoomieTooltip from '@lahaus-roomie/roomie/src/components/RoomieTooltip/index.vue'
import InfoIcon from '@lahaus-roomie/roomie/src/assets/icons/info.svg'

import type { Props } from './types'

const { t } = useI18n()

const props = defineProps<Props>()

const isStratumTooltipVisible = ref(false)
const isAdministrationFeeTooltipVisible = ref(false)
const isDeliveryDateTooltipVisible = ref(false)

const date = new Date(props?.legalInfo?.delivered_at)
const formattedDate = date.toLocaleString('es-ES', {
  month: 'long',
  year: 'numeric'
}).replace(/^\w/, c => c.toUpperCase())

</script>

<script lang="ts">
export default {
  name: 'FeatureDetail'
}
</script>

<template>
  <div class="pd-feature-detail flex flex-col justify-between px-24 lg:px-48 py-32 lg:py-48 w-full">
    <span class="display-6 lg:display-5 text-pine-600 lg:mt-0">{{ t('features') }}</span>

    <span class="body-2-m lg:body-1-m text-pine-600 mt-32 lg:mt-48">{{ listing?.raw_listing?.description }}</span>

    <span class="link-1-sb text-pine-600 mt-16 underline cursor-pointer hidden">{{ t('seeMore') }}</span>

    <hr class="divider text-pine-600 opacity-20 my-32 lg:my-40">

    <div class="flex flex-col flex-1 mt-0">
      <div class="flex flex-col lg:flex-row">
        <div class="flex flex-col flex-1">
          <span class="body-3-sb lg:body-1-m text-pine-600">{{ t('fiduciary') }}:</span>

          <div class="flex flex-row mt-8 items-center">
            <span class="body-1-sb lg:hl-3-sb text-pine-600">{{ listing?.raw_listing?.ims?.fiduciary }}</span>
          </div>
        </div>

        <div class="flex flex-col flex-1 mt-16 lg:mt-0">
          <span class="body-1-m text-pine-600">{{ t('stratum') }}:</span>

          <div class="relative flex flex-row mt-8 items-center mt-8">
            <span class="body-1-sb lg:hl-3-sb text-pine-600">{{ t('stratumLabel') }} {{ legalInfo?.socioeconomic_stratum }}</span>

            <div class="flex flex-row relative">
              <InfoIcon
                class="ml-16 w-20 h-20 fill-pine-600 opacity-50"
                @mouseover="isStratumTooltipVisible = true"
                @click="isStratumTooltipVisible = !isStratumTooltipVisible" />

              <RoomieTooltip
                id="stratum-tooltip"
                data-lh-id="stratum-tooltip"
                class="absolute"
                variant="message"
                :is-visible="isStratumTooltipVisible"
                indicator-placement="bottom left"
                @tooltip-closed="isStratumTooltipVisible = false">
                <template #description>
                  {{ t('stratumTooltip') }}
                </template>
              </RoomieTooltip>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row mt-16 lg:mt-32">
        <div
          class="flex flex-col flex-1">
          <span class="body-3-sb lg:body-1-m text-pine-600">{{ t('administrationFee') }}:</span>

          <div class="relative flex flex-row mt-8 items-center">
            <span class="body-1-sb lg:hl-3-sb text-pine-600">
              {{ legalInfo.estimated_administration_price }}
            </span>

            <div class="flex flex-row relative">
              <InfoIcon
                class="ml-16 w-20 h-20 fill-pine-600 opacity-50"
                @mouseover="isAdministrationFeeTooltipVisible = true"
                @click="isAdministrationFeeTooltipVisible = !isAdministrationFeeTooltipVisible" />

              <RoomieTooltip
                id="administration-fee-tooltip"
                data-lh-id="administration-fee-tooltip"
                class="absolute"
                variant="message"
                :is-visible="isAdministrationFeeTooltipVisible"
                indicator-placement="bottom left"
                @tooltip-closed="isAdministrationFeeTooltipVisible = false">
                <template #description>
                  {{ t('administrationFeeTooltip') }}
                </template>
              </RoomieTooltip>
            </div>
          </div>
        </div>

        <div
          v-if="!!legalInfo?.delivered_at"
          class="flex flex-col flex-1 mt-16 lg:mt-0">
          <span class="body-3-sb lg:body-1-m text-pine-600">{{ t('deliveryDate') }}:</span>

          <div class="relative flex flex-row mt-8 items-center mt-8">
            <span class="body-1-sb lg:hl-3-sb text-pine-600">{{ formattedDate }}</span>

            <div class="flex flex-row relative">
              <InfoIcon
                class="ml-16 w-20 h-20 fill-pine-600 opacity-50"
                @mouseover="isDeliveryDateTooltipVisible = true"
                @click="isDeliveryDateTooltipVisible = !isDeliveryDateTooltipVisible" />

              <RoomieTooltip
                id="delivery-date-tooltip"
                data-lh-id="delivery-date-tooltip"
                class="absolute"
                variant="message"
                :is-visible="isDeliveryDateTooltipVisible"
                indicator-placement="bottom left"
                @tooltip-closed="isDeliveryDateTooltipVisible = false">
                <template #description>
                  {{ t('deliveryDateTooltip') }}
                </template>
              </RoomieTooltip>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row mt-16 lg:mt-24">
        <div class="flex flex-col flex-1">
          <span class="body-3-sb lg:body-1-m text-pine-600">{{ t('stages') }}:</span>

          <div class="flex flex-row mt-8 items-center mt-8">
            <span class="body-1-sb lg:hl-3-sb text-pine-600">{{ legalInfo?.project_stages }}</span>
          </div>
        </div>

        <div class="flex flex-col flex-1 mt-16 lg:mt-0">
          <span class="body-3-sb lg:body-1-m text-pine-600">{{ t('parking') }}:</span>

          <span class="body-1-sb lg:hl-3-sb text-pine-600 mt-8">{{ legalInfo?.parking_details }}</span>
        </div>
      </div>

      <hr class="divider text-pine-600 opacity-20 my-32 lg:my-40">

      <div class="flex flex-col lg:flex-row">
        <div class="flex flex-col flex-1">
          <span class="body-3-m lg:body-1-sb text-pine-600">{{ t('finishingTouch') }}</span>

          <span class="body-2-m lg:body-1-m text-pine-600 mt-16 max-w-364">{{ legalInfo?.finishes_and_details?.description }}</span>

          <span class="link-1-sb text-pine-600 mt-16 underline cursor-pointer hidden">{{ t('seeDetails') }}</span>
        </div>

        <div class="flex flex-col flex-1 mt-24 lg:mt-0">
          <span class="body-3-m lg:body-1-sb text-pine-600">{{ t('penalty') }}</span>

          <ul class="mt-16 max-w-364">
            <li>
              <span class="body-2-m lg:body-1-m text-pine-600 mt-16">{{ legalInfo?.penalties_and_surcharges }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n src="./i18n.json" lang="json" />
